/**
 * Client-side image pre-processor for the "Fill from photo" workflow.
 *
 * Why we don't just upload the raw camera file:
 *   - A modern phone produces 4–8 MB JPEGs (or HEICs); gpt-4o tokenizes
 *     images by tiles, so larger inputs are noticeably more expensive
 *     per call without improving recognition of label text.
 *   - Office Wi-Fi can be slow; round-trip latency for 5 MB uploads
 *     materially hurts the UX.
 *   - HEIC from iPhones isn't accepted by every server-side decoder.
 *     Re-encoding to JPEG normalizes the upload format.
 *
 * The 1280-px longest-edge target is empirically enough for label OCR
 * with gpt-4o while typically cutting the payload to a few hundred KB.
 */

const MAX_LONGEST_EDGE = 1280;
const JPEG_QUALITY = 0.8;

export interface PreprocessedImage {
    readonly blob: Blob;
    readonly contentType: string;
    /** Original file name, preserved as the multipart field's filename. */
    readonly fileName: string;
}

export async function preprocessImageForUpload(file: File): Promise<PreprocessedImage> {
    // `imageOrientation: "from-image"` is critical: iPhone photos rotate
    // via EXIF metadata that the canvas does NOT honor by default.
    // Without this, portrait shots arrive sideways at the recognizer.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });

    try {
        const { width, height } = scaleToFit(bitmap.width, bitmap.height, MAX_LONGEST_EDGE);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx === null) {
            throw new Error("Failed to acquire 2D rendering context for image resize.");
        }
        ctx.drawImage(bitmap, 0, 0, width, height);

        const blob = await canvasToJpegBlob(canvas);
        return {
            blob,
            contentType: "image/jpeg",
            // Strip extension and re-suffix; the source might be .heic
            // and we've re-encoded to JPEG.
            fileName: stripExtension(file.name) + ".jpg",
        };
    } finally {
        bitmap.close();
    }
}

function scaleToFit(width: number, height: number, maxEdge: number): { width: number; height: number } {
    const longest = Math.max(width, height);
    if (longest <= maxEdge) return { width, height };
    const scale = maxEdge / longest;
    return {
        width: Math.round(width * scale),
        height: Math.round(height * scale),
    };
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob === null) {
                    reject(new Error("Canvas toBlob returned null while encoding JPEG."));
                    return;
                }
                resolve(blob);
            },
            "image/jpeg",
            JPEG_QUALITY,
        );
    });
}

function stripExtension(fileName: string): string {
    const dot = fileName.lastIndexOf(".");
    return dot > 0 ? fileName.slice(0, dot) : fileName;
}
